import { Component, Inject, OnInit } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { StammkundeService } from '../../../../Services/stammkunde.service';
import { TakedaExportEinsatzOption } from '../../../../Models/stammkunde.model';
import { speichereTakedaExportDatei, takedaExportFehlerText } from '../takeda-export-download.util';

export interface TakedaExcelExportDialogData {
  auftragId: number;
  auftragTitel?: string | null;
}

/** Hauptauswahl: Gesamtübersicht oder ein Blatt je Einsatz. */
export type TakedaExcelExportModus = 'gesamt' | 'einsaetze';

/** Unterauswahl bei „Gesamtübersicht“: nur das Übersichtsblatt oder zusätzlich ein Blatt je Einsatz. */
export type TakedaExcelGesamtModus = 'nur' | 'mitEinsaetzen';

/** Unterauswahl bei „Nach Einsätzen“. */
export type TakedaExcelEinsatzModus = 'einzeln' | 'alle';

/**
 * Exportdialog der Takeda-Regieabrechnung.
 *
 * Der Download läuft hier im Dialog: so sieht der Benutzer den Ladezustand an
 * der Stelle, an der er geklickt hat, Fehler erscheinen im Dialog, und ein
 * zweiter Klick während des Exports ist ausgeschlossen. Nach erfolgreichem
 * Download schließt sich der Dialog mit `true`.
 *
 * Die Einsätze werden vom Server geladen – aus derselben Abrechnung, die auch
 * exportiert wird. Es gibt keine fest codierten Einsätze.
 */
@Component({
  selector: 'app-takeda-excel-export-dialog',
  templateUrl: './takeda-excel-export-dialog.component.html',
  styleUrls: ['./takeda-excel-export-dialog.component.css', '../../stammkunden-theme.css']
})
export class TakedaExcelExportDialogComponent implements OnInit {
  modus: TakedaExcelExportModus = 'gesamt';
  gesamtModus: TakedaExcelGesamtModus = 'nur';
  einsatzModus: TakedaExcelEinsatzModus = 'einzeln';
  einsatzId: number | null = null;

  einsaetze: TakedaExportEinsatzOption[] = [];
  einsaetzeLaden = true;
  einsaetzeFehler: string | null = null;

  exportLaeuft = false;
  fehler: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<TakedaExcelExportDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: TakedaExcelExportDialogData,
    private stammkundeService: StammkundeService
  ) {}

  ngOnInit(): void {
    this.stammkundeService.getTakedaExportEinsaetze(this.data.auftragId).subscribe({
      next: einsaetze => {
        this.einsaetze = einsaetze ?? [];
        this.einsaetzeLaden = false;

        // Bei genau einem Einsatz gibt es nichts zu wählen – direkt vorbelegen.
        if (this.einsaetze.length === 1) {
          this.einsatzId = this.einsaetze[0].einsatzId;
        }
      },
      error: err => {
        this.einsaetze = [];
        this.einsaetzeLaden = false;
        this.einsaetzeFehler = takedaExportFehlerText(err, 'Die Einsätze konnten nicht geladen werden.');
      }
    });
  }

  get keineEinsaetze(): boolean {
    return !this.einsaetzeLaden && !this.einsaetzeFehler && this.einsaetze.length === 0;
  }

  /** Exportbutton nur, wenn die Auswahl vollständig ist. */
  get kannExportieren(): boolean {
    if (this.exportLaeuft) { return false; }
    if (this.modus === 'gesamt') { return true; }

    if (this.einsaetzeLaden || this.einsaetze.length === 0) { return false; }
    if (this.einsatzModus === 'alle') { return true; }

    return this.einsatzId != null && this.einsaetze.some(e => e.einsatzId === this.einsatzId);
  }

  auswahlGeaendert(): void {
    this.fehler = null;
  }

  zeitraum(e: TakedaExportEinsatzOption): string {
    const von = this.datum(e.von);
    const bis = this.datum(e.bis);
    return von === bis ? von : `${von} – ${bis}`;
  }

  exportieren(): void {
    if (!this.kannExportieren) { return; }

    this.exportLaeuft = true;
    this.fehler = null;
    this.dialogRef.disableClose = true;

    this.anfrage().subscribe({
      next: response => {
        const gespeichert = speichereTakedaExportDatei(response, 'Takeda-Abrechnung.xlsx');
        this.exportBeendet();

        if (!gespeichert) {
          this.fehler = 'Die Excel-Datei war leer.';
          return;
        }

        this.dialogRef.close(true);
      },
      error: err => {
        this.exportBeendet();
        this.fehler = takedaExportFehlerText(err, 'Die Excel-Datei konnte nicht erzeugt werden.');
      }
    });
  }

  abbrechen(): void {
    if (this.exportLaeuft) { return; }
    this.dialogRef.close(false);
  }

  private anfrage(): Observable<HttpResponse<Blob>> {
    const id = this.data.auftragId;

    if (this.modus === 'gesamt') {
      return this.gesamtModus === 'nur'
        ? this.stammkundeService.downloadTakedaExcelUebersicht(id)
        : this.stammkundeService.downloadTakedaExcel(id);
    }
    if (this.einsatzModus === 'alle') {
      return this.stammkundeService.downloadTakedaExcelAlleEinsaetze(id);
    }
    return this.stammkundeService.downloadTakedaExcelEinsatz(id, this.einsatzId!);
  }

  private exportBeendet(): void {
    this.exportLaeuft = false;
    this.dialogRef.disableClose = false;
  }

  private datum(iso: string | null | undefined): string {
    if (!iso) { return '–'; }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '–' : d.toLocaleDateString('de-DE');
  }
}
